import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { callbackQuery } from "../../../../supabase/functions/_shared/gmail/match.ts";
import {
  connectMailbox,
  sentMailSummary,
  type GmailSyncResult,
} from "./gmailApi";

type CallbackQuery = {
  code: string | null;
  state: string | null;
  googleError: string | null;
};

let rememberedQuery: CallbackQuery | null = null;

const takeCallbackQuery = (): CallbackQuery => {
  if (rememberedQuery) return rememberedQuery;
  const query = callbackQuery(window.location.search, window.location.hash);
  rememberedQuery = {
    code: query.code,
    state: query.state,
    googleError: query.error,
  };
  if (
    rememberedQuery.code ||
    rememberedQuery.state ||
    rememberedQuery.googleError
  ) {
    const cleanHash = window.location.hash.split("?")[0] || "#/gmail/callback";
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${cleanHash}`,
    );
  }
  return rememberedQuery;
};

type Phase =
  | { status: "working"; detail: string }
  | { status: "done"; googleEmail: string; summary: string }
  | { status: "error"; detail: string };

export const GmailCallbackPage = () => {
  const [phase, setPhase] = useState<Phase>({
    status: "working",
    detail: "Connecting Gmail…",
  });

  useEffect(() => {
    let cancelled = false;
    const show = (next: Phase) => {
      if (!cancelled) setPhase(next);
    };
    const query = takeCallbackQuery();
    if (query.googleError) {
      show({
        status: "error",
        detail:
          "Google sign-in was cancelled. Open Settings and choose Sign in with Google to pick the account.",
      });
      return;
    }
    if (!query.code || !query.state) {
      show({
        status: "error",
        detail:
          "Google did not return a sign-in code. Open Settings and choose Sign in with Google again.",
      });
      return;
    }
    show({
      status: "working",
      detail: "Reading sent mail from the account you picked…",
    });
    connectMailbox(query.code, query.state)
      .then((result: GmailSyncResult) =>
        show({
          status: "done",
          googleEmail: result.googleEmail,
          summary: sentMailSummary(result),
        }),
      )
      .catch((error: unknown) =>
        show({
          status: "error",
          detail:
            error instanceof Error
              ? error.message
              : "Gmail could not be connected.",
        }),
      );
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-xl mx-auto mt-8">
      <Card>
        <CardHeader>
          <CardTitle>Gmail</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {phase.status === "done" ? (
            <>
              <p className="text-sm">
                Reading sent mail from {phase.googleEmail}.
              </p>
              <p className="text-sm">{phase.summary}</p>
            </>
          ) : (
            <p className="text-sm">{phase.detail}</p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/settings">Settings</Link>
            </Button>
            {phase.status === "done" ? (
              <Button asChild>
                <Link to="/contacts">Leads</Link>
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

GmailCallbackPage.path = "/gmail/callback";
